import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

const CitizenStatsTable = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions for info popups
  const definitions = {
    "State Wise Citizen:Services Distribution": {
      title: "State Wise Citizen:Services Distribution",
      description: "This dashboard shows the distribution of citizens across different service categories by state. It helps understand how citizens are utilizing various services.",
      example: "• States: Maharashtra, Karnataka, Delhi, etc.\n• Service categories: Unique Citizens, 1+ Services, 2+ Services, 3+ Services, 4+ Services"
    },
    "State": {
      title: "State",
      description: "The state or union territory where citizens are located and accessing services.",
      example: "Example: Maharashtra, Karnataka, Tamil Nadu, Delhi, etc."
    },
    "Total Unique Citizens": {
      title: "Total Unique Citizens",
      description: "The total number of unique citizens in each state who have accessed at least one service. Each citizen is counted only once regardless of how many services they have used.",
      example: "If a citizen has accessed 5 different services, they are counted only once in this column."
    },
    "Citizens with 1+ Services": {
    title: "Citizens with 1+ Services",
    description: "Citizens who have received at least one service from any category. This does not include citizens who have only received the EDP service.",
    example: "DFL or Yojana Card or Scheme/Document service"
    },
    "Citizens with 2+ Services": {
    title: "Citizens with 2+ Services",
    description: "Citizens who have received services from at least two service categories.EDP service is not included here .",
    example: "DFL + YC+ schemes(0 or greater than 0), DFL + Schemes(1 or greater than 1), or YC + Schemes(1 or greater than 1)"
      },
    "Citizens with 3+ Services": {
      title: "Citizens with 3+ Services",
      description: "Citizens who have accessed 3 or more services. This includes: DFL + Yojana Card + at least 1 Scheme service.EDP service is not included here .",
      example: "• DFL Service\n• Yojana Card\n• 1 or more Scheme services\nTotal: 3+ services"
    },
    "Citizens with 4+ Services": {
      title: "Citizens with 4+ Services",
      description: "Citizens who have accessed 4 or more services. This includes: DFL + Yojana Card + at least 2 Scheme services.EDP service is not included here .",
      example: "• DFL Service\n• Yojana Card\n• 2 or more Scheme services\nTotal: 4+ services"
    }
  };

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/combine-citizens/?pid=${pid}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (pid) {
      fetchData();
    }
  }, [pid]);

  const formatNumber = (num) => {
    return num?.toLocaleString() ?? '0';
  };

  const formatPercent = (num) => {
    return num?.toFixed(2) ?? '0.00';
  };

  return (
    <div className="p-6 mt-3 bg-white rounded-lg border-2 max-w-[90vw] ml-[3vw] border-gray-200 shadow-md shadow-black">
      
      {/* Header */}
      <div className="flex items-center mb-6 border-b pb-2">
        <h2 className="text-xl font-bold text-pink-800">State Wise Citizen:Services Distribution</h2>
        <InfoButton title="State Wise Citizen:Services Distribution" />
      </div>
      
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded" role="alert">
          <p>Error loading data: {error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-50">
              <tr>
                <th className="py-3 px-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  <div className="flex items-center">
                    <span>State</span>
                    <InfoButton title="State" />
                  </div>
                </th>
                <th className="py-3 px-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  <div className="flex items-center justify-end">
                    <span>Total Unique Citizens</span>
                    <InfoButton title="Total Unique Citizens" />
                  </div>
                </th>
                <th className="py-3 px-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  <div className="flex items-center justify-end">
                  <span>Citizens with 1+ Services</span>
                  <InfoButton title="Citizens with 1+ Services" />
                  </div>
                </th>

                <th className="py-3 px-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  <div className="flex items-center justify-end">
                  <span>Citizens with 2+ Services</span>
                  <InfoButton title="Citizens with 2+ Services" />
                  </div>
                </th>
                <th className="py-3 px-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  <div className="flex items-center justify-end">
                    <span>Citizens with 3+ Services</span>
                    <InfoButton title="Citizens with 3+ Services" />
                  </div>
                </th>
                <th className="py-3 px-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">
                  <div className="flex items-center justify-end">
                    <span>Citizens with 4+ Services</span>
                    <InfoButton title="Citizens with 4+ Services" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr 
                  key={index} 
                  className={item.state === 'Grand Total' ? 'bg-blue-50 font-semibold hover:bg-blue-100' : 'hover:bg-blue-50 transition-colors'}
                >
                  <td className="py-3 px-4 text-sm text-gray-800 border-r border-gray-200">
                    {item.state}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800 text-right border-r border-gray-200">
                    {formatNumber(item.total_citizens)}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800 text-right border-r border-gray-200">
                    {formatNumber(item.citizens_1plus_services)}
                  </td>

                  <td className="py-3 px-4 text-sm text-gray-800 text-right border-r border-gray-200">
                    {formatNumber(item.citizens_2plus_services)}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800 text-right border-r border-gray-200">
                    {formatNumber(item.citizens_3plus_services)}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800 text-right">
                    {formatNumber(item.citizens_4plus_services)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default CitizenStatsTable;